import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

const getAccessConfig = () => {
  if (!localStorage.user) {
    return { headers: {} };
  }

  const parsedUser = JSON.parse(localStorage.user);

  return {
    headers: {
      Authorization: `Bearer ${parsedUser.access_token}`,
    },
  };
};

const normalizeProfile = (profile) => {
  const firstName =
    profile?.user_first_name ||
    profile?.first_name ||
    profile?.user?.first_name ||
    "";
  const lastName =
    profile?.user_last_name ||
    profile?.last_name ||
    profile?.user?.last_name ||
    "";
  const fullName =
    `${firstName} ${lastName}`.trim() ||
    profile?.full_name ||
    profile?.user?.full_name ||
    "N/A";
  const email =
    profile?.email || profile?.user?.email || profile?.user || "N/A";

  return {
    id: profile?.name,
    fullName,
    email,
    contactNumber: profile?.contact_number || "N/A",
  };
};

const AuditLog = () => {
  const { eventID } = useParams();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const tableRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [profiles]);

  const handleSearch = async (event) => {
    event.preventDefault();

    const trimmedEmail = searchEmail.trim().toLowerCase();

    if (!trimmedEmail) {
      setProfiles([]);
      setHasSearched(false);
      setError(false);
      return;
    }

    try {
      setLoading(true);
      setError(false);
      setHasSearched(true);

      const filters = encodeURIComponent(
        JSON.stringify([["email", "like", `%${trimmedEmail}%`]])
      );

      const response = await axios.get(
        `${window.$domain}/api/resource/Delegate Profile?fields=%5B%22*%22%5D&filters=${filters}&limit_page_length=100&order_by=modified desc`,
        getAccessConfig()
      );

      setProfiles(
        (response?.data?.data || [])
          .map(normalizeProfile)
          .filter((profile) => profile.id)
      );
    } catch (fetchError) {
      console.log(fetchError);
      setProfiles([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchEmail("");
    setProfiles([]);
    setHasSearched(false);
    setError(false);
  };

  if (localStorage.role !== "Operations Member") {
    return (
      <div className="rounded-2xl border border-red-100 bg-red-50 p-6">
        <h2 className="text-xl font-semibold text-red-900">Restricted Page</h2>
        <p className="mt-2 text-sm text-red-700">
          Audit Log is only available for the Operations Member role.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="px-2">
        <div className="flex flex-col gap-4 py-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Audit Log</h2>
            <p className="mt-1 text-sm text-gray-500">
              Search by delegate email and open the matching profile to review
              its audit trail.
            </p>
          </div>

          <form
            onSubmit={handleSearch}
            className="flex w-full max-w-2xl flex-col gap-3 sm:flex-row sm:items-end"
          >
            <div className="w-full">
              <label htmlFor="audit-log-search" className="sr-only">
                Search by delegate email
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  id="audit-log-search"
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  value={searchEmail}
                  onChange={(event) => setSearchEmail(event.target.value)}
                  placeholder="Search by delegate email"
                  className="w-full rounded-md border border-gray-300 bg-white py-2.5 pl-12 pr-4 text-sm text-gray-900 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={loading}
                className="rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
              >
                {loading ? "Searching..." : "Search"}
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="rounded-md border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
              >
                Clear
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-1 py-1 divide-y">
        <p className="felx justify-end px-2 text-right font-semibold text-gray-600">
          {hasSearched ? `Profiles: ${profiles.length}` : "Profiles: 0"}
        </p>
      </div>

      <div className="px-2">
        <div className="mt-2 flex flex-col">
          <div
            ref={tableRef}
            className="-my-2 -mx-4 h-[40rem] overflow-x-auto overflow-y-auto scrollbar-div-color sm:-mx-6 lg:-mx-8"
          >
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-6">
              {error ? (
                <div className="flex h-full min-h-[36rem] flex-col items-center justify-center">
                  <svg
                    fill="none"
                    className="h-32 w-32 text-gray-400"
                    stroke="currentColor"
                    strokeWidth={1.5}
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zM12 15.75h.008v.008H12v-.008z"
                    />
                  </svg>
                  <span className="mt-3 text-lg text-gray-400">
                    Unable to load audit log
                  </span>
                </div>
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-stone-100">
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                        >
                          Name
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                        >
                          Email
                        </th>
                        <th
                          scope="col"
                          className="px-3 py-3.5 whitespace-nowrap text-left text-sm font-semibold text-gray-900"
                        >
                          Contact Number
                        </th>
                      </tr>
                    </thead>

                    {loading ? (
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {[...Array(10)].map((_, index) => (
                          <tr key={index}>
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                              <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <div className="h-4 w-52 animate-pulse rounded bg-gray-200" />
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <div className="h-4 w-32 animate-pulse rounded bg-gray-200" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    ) : !hasSearched ? (
                      <tbody className="divide-y divide-gray-200 bg-white">
                        <tr>
                          <td colSpan={3} className="px-6 py-24 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <svg
                                fill="none"
                                className="h-28 w-28 text-gray-400"
                                stroke="currentColor"
                                strokeWidth={1.5}
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M15.75 15.75 18 18m-6.75-1.5a6.75 6.75 0 1 1 0-13.5 6.75 6.75 0 0 1 0 13.5Z"
                                />
                              </svg>
                              <span className="mt-3 text-lg text-gray-400">
                                Search by delegate email to view results
                              </span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    ) : profiles.length === 0 ? (
                      <tbody className="divide-y divide-gray-200 bg-white">
                        <tr>
                          <td colSpan={3} className="px-6 py-24 text-center">
                            <div className="flex flex-col items-center justify-center">
                              <svg
                                fill="none"
                                className="h-28 w-28 text-gray-400"
                                stroke="currentColor"
                                strokeWidth={1.5}
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5"
                                />
                              </svg>
                              <span className="mt-3 text-lg text-gray-400">
                                No delegate found for this email
                              </span>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    ) : (
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {profiles.map((profile) => (
                          <tr key={profile.id} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                              <Link
                                to={`/OperationsDashboard/${eventID}/audit-log/${eventID}/${profile.id}`}
                                state={{ profile }}
                                className="block"
                              >
                                <div className="font-medium cursor-pointer text-gray-900">
                                  {profile.fullName}
                                </div>
                                <div className="text-xs text-gray-500">
                                  Open profile
                                </div>
                              </Link>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <Link
                                to={`/OperationsDashboard/${eventID}/audit-log/${eventID}/${profile.id}`}
                                state={{ profile }}
                                className="block"
                              >
                                {profile.email}
                              </Link>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                              <Link
                                to={`/OperationsDashboard/${eventID}/audit-log/${eventID}/${profile.id}`}
                                state={{ profile }}
                                className="block"
                              >
                                {profile.contactNumber}
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    )}
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLog;
